import { useCallback, useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '../../api/client'
import { describeApiFailure } from '../../lib/failure'
import { encounterKeys } from '../encounters/queries'
import { liveKeys } from './queries'
import type { LiveSession } from './types'

/**
 * Writing to a character from the battle card.
 *
 * **These are the ordinary character mutations**, not new session-scoped ones — the same
 * endpoints the character sheet calls, with the same owner-only rule
 * (`CharacterMutationControllerBase`). That is the whole reason `canEdit` on the card is
 * trustworthy: a Game Master who is offered a stress box is offered it because the API would
 * accept the write, not because a flag said so.
 *
 * A write invalidates the **encounter's cards**, because that is where the card came from. It
 * does not touch the live session: stress is a fact about a character, and the session holds
 * positions and turns. Two different things, two different caches.
 */
export function useCardEditing(characterId: number, sessionId: string) {
  const queryClient = useQueryClient()
  const [failure, setFailure] = useState<string | null>(null)

  const refresh = () => {
    setFailure(null)
    void queryClient.invalidateQueries({ queryKey: encounterKeys.cardSets() })
  }

  const onError = (error: unknown) => {
    setFailure(
      describeApiFailure(error, 'That character is not yours to change — it belongs to a player.'),
    )
  }

  const setStress = useMutation({
    mutationFn: (input: { slotId: number; isStressed: boolean }) =>
      api.put(`/characters/${characterId}/stress/${input.slotId}`, {
        isStressed: input.isStressed,
      }),
    onSuccess: refresh,
    onError,
  })

  const clearConsequence = useMutation({
    mutationFn: (input: { slotId: number }) =>
      api.delete(`/characters/${characterId}/consequences/${input.slotId}/consequence`),
    onSuccess: refresh,
    onError,
  })

  /**
   * Fills an empty slot.
   *
   * The API refuses a slot that already holds one (409), so changing the wording of a consequence
   * is clear-then-set. That is the sheet's rule and not a new one — and at a table it is the right
   * shape anyway: a consequence is taken, not edited.
   */
  const setConsequence = useMutation({
    mutationFn: (input: { slotId: number; name: string }) =>
      api.put(`/characters/${characterId}/consequences/${input.slotId}`, {
        name: input.name,
        effect: null,
      }),
    onSuccess: refresh,
    onError,
  })

  // Read but not written here — held so a caller can key off the session without importing the
  // live queries as well.
  const session = queryClient.getQueryData<LiveSession>(liveKeys.session(sessionId))

  return {
    setStress,
    clearConsequence,
    setConsequence,
    failure,
    dismiss: useCallback(() => setFailure(null), []),
    busy: setStress.isPending || clearConsequence.isPending || setConsequence.isPending,
    canControlSession: session?.canControl === true,
  }
}
